import Seo from "@/components/feature/Seo";
import Layout from "@/components/feature/Layout";
import ComicCard from "@/components/feature/ComicCard";
import SectionHeader from "@/components/base/SectionHeader";
import { comics } from "@/mocks/comics";
import { useNavigate } from "react-router-dom";

export default function About() {
  const navigate = useNavigate();
  const picks = comics.slice(0, 4);

  const jsonLd = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "AboutPage",
        name: "关于笨狗漫画网",
        url: "https://manhwanalysis.github.io/about",
        inLanguage: "zh-CN",
        description:
          "了解笨狗漫画网的品牌定位、发展理念、内容原则与用户口碑，追更不迷路的漫画阅读平台。",
      },
      {
        "@type": "Organization",
        name: "笨狗漫画网",
        url: "https://manhwanalysis.github.io/",
        contactPoint: {
          "@type": "ContactPoint",
          email: "support@bengou-manga.site",
          contactType: "customer service",
        },
      },
    ],
  };

  return (
    <Layout>
      <Seo
        title="关于我们 | 笨狗漫画网 · 追更不迷路的漫画阅读平台"
        description="了解笨狗漫画网的品牌定位、发展理念、内容原则与用户口碑。我们专注为漫画爱好者提供一目了然的每日更新列表，追更不迷路，快速找到你想看的漫画。"
        keywords="笨狗漫画网,关于我们,漫画平台,今日更新,漫画阅读,追更"
        canonical="https://manhwanalysis.github.io/about"
        jsonLd={jsonLd}
      />

      <section className="bg-background-100">
        <div className="mx-auto max-w-7xl px-4 py-10 md:px-6">
          <nav className="flex items-center gap-1 text-sm text-foreground-500">
            <button onClick={() => navigate("/")} className="hover:text-primary-600 cursor-pointer">
              首页
            </button>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-foreground-900">关于我们</span>
          </nav>
          <h1 className="mt-3 font-heading text-2xl font-extrabold text-foreground-950 md:text-3xl">
            关于笨狗漫画网
          </h1>
          <p className="mt-2 max-w-2xl text-sm text-foreground-600">
            一个让"追更不迷路"的漫画更新门户，最新漫画、连载、完结作品随时掌握。
          </p>
        </div>
      </section>

      {/* 品牌介绍 */}
      <section className="mx-auto max-w-4xl px-4 py-14 md:px-6">
        <SectionHeader title="品牌介绍" accent />
        <div className="rounded-xl border border-background-200 bg-background-50 p-6 md:p-8">
          <p className="text-sm leading-relaxed text-foreground-700">
            笨狗漫画网是一个专注于漫画更新的内容平台。我们深知，对于漫画爱好者来说，最困扰的往往不是"找不找得到好漫画"，而是"心仪的作品到底更新了没有"。笨狗漫画网把"每日更新列表"放在最显眼的位置，让每一部作品的连载动态都一目了然。
          </p>
          <p className="mt-4 text-sm leading-relaxed text-foreground-700">
            从热血冒险到悬疑推理，从萌系日常到科幻机甲，我们持续收录并整理优质的连载与完结作品，并通过清晰的状态标签（连载中、已完结、新上架）与类型分类，帮助每一位读者快速定位自己想看的漫画。
          </p>
        </div>
      </section>

      {/* 定位与理念 */}
      <section className="bg-background-100">
        <div className="mx-auto max-w-7xl px-4 py-14 md:px-6">
          <SectionHeader title="网站定位与发展理念" />
          <div className="grid grid-cols-1 gap-5 md:grid-cols-3">
            {[
              {
                icon: "ri-focus-3-line",
                title: "更新优先",
                text: "把「每日更新」作为核心，实时汇总连载动态，让你点开首页就知道今天有哪些新章节上架，追更不迷路。",
              },
              {
                icon: "ri-organization-chart",
                title: "分类清晰",
                text: "以类型、状态双维度组织内容，搭配关键词搜索，帮助你在海量作品中快速找到感兴趣的漫画。",
              },
              {
                icon: "ri-user-heart-line",
                title: "读者为本",
                text: "一切功能以读者的阅读体验为出发点，简洁的浏览路径、一目了然的更新信息，让找漫画和追更都变得轻松愉快。",
              },
            ].map((item) => (
              <div
                key={item.title}
                className="rounded-xl border border-background-200 bg-background-50 p-6 transition-all hover:-translate-y-1 hover:border-primary-300"
              >
                <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-primary-100 text-primary-700">
                  <i className={`${item.icon} text-2xl`}></i>
                </span>
                <h3 className="mt-4 font-heading text-base font-bold text-foreground-950">
                  {item.title}
                </h3>
                <p className="mt-2 text-sm leading-relaxed text-foreground-600">{item.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 内容原则 */}
      <section className="mx-auto max-w-7xl px-4 py-14 md:px-6">
        <SectionHeader title="内容原则" />
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {[
            { icon: "ri-check-double-line", text: "及时收录最新漫画与连载更新" },
            { icon: "ri-shield-check-line", text: "严格审核内容，保障作品信息准确" },
            { icon: "ri-tools-line", text: "持续完善分类与检索体验" },
            { icon: "ri-community-line", text: "尊重每一位作者与读者" },
          ].map((item) => (
            <div
              key={item.text}
              className="flex items-center gap-3 rounded-xl border border-background-200 bg-background-50 px-5 py-4"
            >
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-accent-100 text-accent-700">
                <i className={`${item.icon} text-xl`}></i>
              </span>
              <p className="text-sm font-medium text-foreground-800">{item.text}</p>
            </div>
          ))}
        </div>
      </section>

      {/* 用户口碑 */}
      <section className="bg-background-100">
        <div className="mx-auto max-w-7xl px-4 py-14 md:px-6">
          <SectionHeader title="用户口碑" subtitle="来自广大漫画读者的真实反馈" />
          <div className="grid grid-cols-1 gap-5 md:grid-cols-3">
            {[
              {
                name: "夜行猫",
                role: "热血漫爱好者",
                text: "首页的今日更新列表太实用了，每天打开就知道追的漫画有没有更新，再也不用一个个点进去看了。",
              },
              {
                name: "墨白",
                role: "推理迷",
                text: "分类很清楚，连载和完结分开，搜索也很快，基本想找什么漫画都能一眼定位到，体验很顺手。",
              },
              {
                name: "小鹿",
                role: "萌系漫爱好者",
                text: "新上架推荐总能发现宝藏作品，更新的章节也都标注得很清楚，追更完全不迷路，很赞。",
              },
            ].map((t) => (
              <figure
                key={t.name}
                className="rounded-xl border border-background-200 bg-background-50 p-6"
              >
                <div className="flex items-center gap-1 text-accent-500">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <i key={i} className="ri-star-fill text-sm"></i>
                  ))}
                </div>
                <blockquote className="mt-4 text-sm leading-relaxed text-foreground-700">
                  "{t.text}"
                </blockquote>
                <figcaption className="mt-4 flex items-center gap-3">
                  <span className="flex h-10 w-10 items-center justify-center rounded-full bg-primary-100 text-sm font-bold text-primary-700">
                    {t.name[0]}
                  </span>
                  <div>
                    <p className="text-sm font-bold text-foreground-950">{t.name}</p>
                    <p className="text-xs text-foreground-500">{t.role}</p>
                  </div>
                </figcaption>
              </figure>
            ))}
          </div>
        </div>
      </section>

      {/* 热门精选 */}
      <section className="mx-auto max-w-7xl px-4 py-14 md:px-6">
        <SectionHeader title="平台热门精选" subtitle="口碑与人气俱佳的作品，值得一看" />
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
          {picks.map((c) => (
            <ComicCard key={c.id} comic={c} />
          ))}
        </div>
      </section>

      {/* 联系方式 */}
      <section className="bg-background-100">
        <div className="mx-auto max-w-4xl px-4 py-14 md:px-6">
          <SectionHeader title="联系我们" accent />
          <div className="rounded-xl border border-background-200 bg-background-50 p-6 md:p-8">
            <p className="text-sm text-foreground-600">
              如果你对笨狗漫画网有任何建议、意见或合作意向，欢迎通过以下方式与我们取得联系，我们期待听到你的声音。
            </p>
            <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="flex items-center gap-3 rounded-lg bg-background-100 px-4 py-3">
                <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary-100 text-primary-700">
                  <i className="ri-mail-line text-xl"></i>
                </span>
                <div>
                  <p className="text-xs text-foreground-500">邮箱</p>
                  <p className="text-sm font-medium text-foreground-900">
                    support@bengou-manga.site
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-3 rounded-lg bg-background-100 px-4 py-3">
                <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary-100 text-primary-700">
                  <i className="ri-time-line text-xl"></i>
                </span>
                <div>
                  <p className="text-xs text-foreground-500">服务时间</p>
                  <p className="text-sm font-medium text-foreground-900">每日 09:00–22:00</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
}